import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck, Upload } from 'lucide-react';
import { DateDisplay } from '@/shared/display/DateDisplay';
import { getCertMeta, uploadCert } from '../certificates/certificates.api';

export function CertificateTab(): JSX.Element {
  const qc = useQueryClient();
  const meta = useQuery({ queryKey: ['cert-meta'], queryFn: getCertMeta });

  const [pfxFile, setPfxFile] = useState<File | null>(null);
  const [pfxPassword, setPfxPassword] = useState('');
  const [sunatUser, setSunatUser] = useState('');
  const [sunatPassword, setSunatPassword] = useState('');

  const upload = useMutation({
    mutationFn: async () => {
      if (!pfxFile) throw new Error('Selecciona un archivo .pfx');
      const buf = await pfxFile.arrayBuffer();
      const b64 = arrayBufferToBase64(buf);
      return uploadCert({ pfxBase64: b64, pfxPassword, sunatSolUser: sunatUser || undefined, sunatSolPassword: sunatPassword || undefined });
    },
    onSuccess: () => {
      toast.success('Certificado guardado y validado');
      qc.invalidateQueries({ queryKey: ['cert-meta'] });
      setPfxFile(null);
      setPfxPassword('');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? (err as Error).message;
      toast.error(msg);
    },
  });

  const status = meta.data;
  const expiringSoon = status?.daysUntilExpiry !== null && (status?.daysUntilExpiry ?? 99) < 30;

  return (
    <div className="cx">
      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-head">
          <div className="card-title">Certificado SUNAT</div>
        </div>
        <div className="card-pad">
          {status?.hasCertificate && (
            <div className="row aic gap12" style={{ padding: 16, background: 'var(--success-soft)', borderRadius: 12, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: '#fff', color: '#15803D', display: 'grid', placeItems: 'center', flex: 'none' }}>
                <ShieldCheck size={20} />
              </div>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="fw7" style={{ color: '#15803D' }}>
                  Certificado activo
                </div>
                <div className="t-xs" style={{ color: '#166534' }}>
                  {status.notAfter ? (
                    <>
                      Vence <DateDisplay value={status.notAfter} />
                      {status.daysUntilExpiry !== null && ` · ${status.daysUntilExpiry} días`}
                    </>
                  ) : (
                    'Producción'
                  )}
                </div>
              </div>
              <span className={`badge badge-${expiringSoon ? 'amber' : 'green'}`}>
                <span className="bdot" />
                {expiringSoon ? 'Por vencer' : 'Conectado'}
              </span>
            </div>
          )}

          {status?.hasCertificate && (
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
              <Detail label="Titular (CN)" value={status.commonName ?? '—'} />
              <Detail label="Serial" value={status.serialNumber ?? '—'} mono />
              <Detail label="Usuario SOL" value={status.sunatSolUser ?? '—'} />
              <Detail label="Válido desde" value={status.notBefore ? <DateDisplay value={status.notBefore} /> : '—'} />
            </div>
          )}

          <div className="col gap14">
            <div className="t-h2 fw7">{status?.hasCertificate ? 'Reemplazar certificado' : 'Subir certificado'}</div>
            <p className="muted t-sm" style={{ margin: 0 }}>
              Tu archivo .pfx (o .p12) se valida y se cifra con AES-256-GCM antes de almacenarse. La contraseña nunca se guarda en claro.
            </p>
            <div className="field">
              <label className="label">Certificado digital (.pfx / .p12)</label>
              <input
                className="input"
                type="file"
                accept=".pfx,.p12"
                style={{ height: 'auto', padding: '8px 12px' }}
                onChange={(e) => setPfxFile(e.target.files?.[0] ?? null)}
              />
              {pfxFile && (
                <div className="t-xs muted">
                  {pfxFile.name} · {(pfxFile.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>
            <div className="field">
              <label className="label">Contraseña del certificado</label>
              <input className="input" type="password" autoComplete="new-password" value={pfxPassword} onChange={(e) => setPfxPassword(e.target.value)} />
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <div className="fw6 t-sm" style={{ marginBottom: 4 }}>
                Credenciales SUNAT (Clave SOL)
              </div>
              <div className="t-xs muted" style={{ marginBottom: 12 }}>
                Opcional. Sin esto, los XML se firman pero no se envían a SUNAT.
              </div>
              <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="field">
                  <label className="label">Usuario SOL</label>
                  <input className="input" value={sunatUser} placeholder="MODDATOS" autoComplete="off" onChange={(e) => setSunatUser(e.target.value)} />
                </div>
                <div className="field">
                  <label className="label">Contraseña SOL</label>
                  <input className="input" type="password" autoComplete="new-password" value={sunatPassword} onChange={(e) => setSunatPassword(e.target.value)} />
                </div>
              </div>
            </div>

            <button className="btn btn-primary btn-block" disabled={!pfxFile || !pfxPassword || upload.isPending} onClick={() => upload.mutate()}>
              <Upload size={17} />
              {upload.isPending ? 'Validando y cifrando…' : 'Guardar certificado'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }): JSX.Element {
  return (
    <div>
      <div className="t-xs muted">{label}</div>
      <div className={mono ? 'tnum t-sm fw6' : 'fw6 t-sm'} style={{ marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
